import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { BaseMetaViewModel } from '../models/BaseMetaViewModel';
import type { DocumentViewModel } from '../models/DocumentViewModel';
import type { SFCampaignDetailsLandingViewModel } from '../models/SFCampaignDetailsLandingViewModel';
import type { SFCampaignPromotionViewModel } from '../models/SFCampaignPromotionViewModel';
import type { SFCruisesLandingViewModel } from '../models/SFCruisesLandingViewModel';
import type { SffaqSidebarViewModel } from '../models/SffaqSidebarViewModel';
import type { SffaqViewModel } from '../models/SffaqViewModel';
import type { SFFlightLandingCityPageViewModel } from '../models/SFFlightLandingCityPageViewModel';
import type { SFFlightLandingViewModel } from '../models/SFFlightLandingViewModel';
import type { SFFooterProductViewModel } from '../models/SFFooterProductViewModel';
import type { SFFooterViewModel } from '../models/SFFooterViewModel';
import type { SFHeaderFooterCategoryFooterViewModel } from '../models/SFHeaderFooterCategoryFooterViewModel';
import type { SFHeaderViewModel } from '../models/SFHeaderViewModel';
import type { SFHelpAndAdviceViewModel } from '../models/SFHelpAndAdviceViewModel';
import type { SFHomePageCampaignPromotionViewModel } from '../models/SFHomePageCampaignPromotionViewModel';
import type { SFHotelLandingViewModel } from '../models/SFHotelLandingViewModel';
import type { SFStaticPageViewModel } from '../models/SFStaticPageViewModel';
import type { SFTourLandingViewModel } from '../models/SFTourLandingViewModel';
import type { SFVehicleLandingViewModel } from '../models/SFVehicleLandingViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class SfContentsService {
  /**
   * Header
   * Gets header
   * @returns SFHeaderViewModel Success
   * @throws ApiError
   */
  public static async sFheader(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFHeaderViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFHeaderViewModel>({
      method: 'GET',
      url: `/sfcontents/header`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Home Page Campaigns
   * Gets Home Page Campaigns
   * @returns SFHomePageCampaignPromotionViewModel Success
   * @throws ApiError
   */
  public static async sFHomePage(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFHomePageCampaignPromotionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFHomePageCampaignPromotionViewModel>({
      method: 'GET',
      url: `/sfcontents/home-page-campaigns`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Campaigns
   * Gets Campaigns
   * @returns SFCampaignPromotionViewModel Success
   * @throws ApiError
   */
  public static async sFCampaigns(
    {
      slug,
    }: {
      /**
       * Examples: kampanyalar
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFCampaignPromotionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFCampaignPromotionViewModel>({
      method: 'GET',
      url: `/sfcontents/campaigns`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * SFCampaign Details (Landing)
   * Gets campaign detail landing pages by slug
   * @returns SFCampaignDetailsLandingViewModel Success
   * @throws ApiError
   */
  public static async getSfCampaignDetailsSf(
    {
      slug,
    }: {
      /**
       * Examples: shopandflycom-tr-de-ekstra-50-000-mil-hediye
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFCampaignDetailsLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFCampaignDetailsLandingViewModel>({
      method: 'GET',
      url: `/sfcontents/campaigns/detail`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Footer
   * Gets footer
   * @returns SFFooterViewModel Success
   * @throws ApiError
   */
  public static async sFfooter(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFFooterViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFFooterViewModel>({
      method: 'GET',
      url: `/sfcontents/footer`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Category Footer
   * Gets Category Footer
   * @returns SFFooterProductViewModel Success
   * @throws ApiError
   */
  public static async sFCategoryfooter(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFFooterProductViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFFooterProductViewModel>({
      method: 'GET',
      url: `/sfcontents/category-footer`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Header Footer Category Footer
   * Gets Header Footer CategoryFooter
   * @returns SFHeaderFooterCategoryFooterViewModel Success
   * @throws ApiError
   */
  public static async sFHeaderFooterCFooter(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFHeaderFooterCategoryFooterViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFHeaderFooterCategoryFooterViewModel>({
      method: 'GET',
      url: `/sfcontents/header-footer-categoryfooter`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Help And Advice
   * Get Help Question
   * @returns SFHelpAndAdviceViewModel Success
   * @throws ApiError
   */
  public static async sFhelpandadvice(
    {
      type,
    }: {
      /**
       * Type: RotardaMil,AvansMil default: RotardaMil
       */
      type?: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFHelpAndAdviceViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFHelpAndAdviceViewModel>({
      method: 'GET',
      url: `/sfcontents/help-and-advice`,
      query: {
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Static Page Content
   * Get Static Page
   * @returns SFStaticPageViewModel Success
   * @throws ApiError
   */
  public static async getSfStaticPage(
    {
      slug,
    }: {
      /**
       * Examples: kvkk
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFStaticPageViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFStaticPageViewModel>({
      method: 'GET',
      url: `/sfcontents/static`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * FAQ Sidebar
   * Gets sidebar for faq pages,
   * @returns SffaqSidebarViewModel Success
   * @throws ApiError
   */
  public static async getSfFaqSidebar(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SffaqSidebarViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SffaqSidebarViewModel>({
      method: 'GET',
      url: `/sfcontents/faq-sidebar`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * FAQ Page
   * Gets Faq pages Content
   * @returns SffaqViewModel Success
   * @throws ApiError
   */
  public static async getSfFaqPage(
    {
      slug,
    }: {
      /**
       * Examples: gsf-populer-sorular
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SffaqViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SffaqViewModel>({
      method: 'GET',
      url: `/sfcontents/faq`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Document
   * Gets document url or text by type
   * @returns DocumentViewModel Success
   * @throws ApiError
   */
  public static async getSfDocument(
    {
      type,
    }: {
      /**
       * Document Type (AgreementDoc, ConsentDoc, ConsentText,LoyaltyText)
       */
      type: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<DocumentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<DocumentViewModel>({
      method: 'GET',
      url: `/sfcontents/document`,
      query: {
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Flights (Landing)
   * Gets flight landing pages
   * @returns SFFlightLandingViewModel Success
   * @throws ApiError
   */
  public static async getSfFlightLanding(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFFlightLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFFlightLandingViewModel>({
      method: 'GET',
      url: `/sfcontents/landing/flight`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Flights (Landing-City)
   * Gets flight landing City pages
   * @returns SFFlightLandingCityPageViewModel Success
   * @throws ApiError
   */
  public static async getSfFlightLandingCity(
    {
      slug,
    }: {
      /**
       * Examples: Ankara
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFFlightLandingCityPageViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFFlightLandingCityPageViewModel>({
      method: 'GET',
      url: `/sfcontents/landing/flight-city`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Hotel (Landing)
   * Gets hotel landing pages
   * @returns SFHotelLandingViewModel Success
   * @throws ApiError
   */
  public static async getSfHotelLanding(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFHotelLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFHotelLandingViewModel>({
      method: 'GET',
      url: `/sfcontents/landing/hotel`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Tour (Landing)
   * Gets tour landing pages
   * @returns SFTourLandingViewModel Success
   * @throws ApiError
   */
  public static async getSfTourLanding(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFTourLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFTourLandingViewModel>({
      method: 'GET',
      url: `/sfcontents/landing/tour`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Cruises (Landing)
   * Gets tour landing pages
   * @returns SFCruisesLandingViewModel Success
   * @throws ApiError
   */
  public static async getSfCruisesLanding(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFCruisesLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFCruisesLandingViewModel>({
      method: 'GET',
      url: `/sfcontents/landing/cruises`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Vehicle (Landing)
   * Gets vehicle landing pages
   * @returns SFVehicleLandingViewModel Success
   * @throws ApiError
   */
  public static async getSfVehicleLanding(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SFVehicleLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SFVehicleLandingViewModel>({
      method: 'GET',
      url: `/sfcontents/landing/vehicle`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Meta
   * Gets meta infos for SEO
   * @returns BaseMetaViewModel Success
   * @throws ApiError
   */
  public static async getMeta(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<BaseMetaViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<BaseMetaViewModel>({
      method: 'GET',
      url: `/sfcontents/meta`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * New Home Cache
   * Clear Redis Cache for HomePage
   * @returns BaseMetaViewModel Success
   * @throws ApiError
   */
  public static async getNewHomeCache(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<BaseMetaViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<BaseMetaViewModel>({
      method: 'GET',
      url: `/sfcontents/new-home-cache`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
